import { MagnifyingGlass } from '@creditornot/cb-icons';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { useTheme } from 'styled-components';

import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import { useSharedMenuViewTracking } from 'modules/menu/hooks/telemetryHooks';
import { FilterMenu } from 'modules/shared-menu/components/FilterMenu';
import { OFFER_FILTER_MENU_ITEMS } from 'modules/shared-menu/components/FilterMenu/constants';
import { SelectableOfferFilterId } from 'modules/shared-menu/components/FilterMenu/types';
import { SelectableAccordion, useSelectableAccordion } from 'modules/shared-menu/components/SelectableAccordion';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useIsWoltSpecialsInListingManagerEnabled } from 'modules/shared-menu/hooks/useIsWoltSpecialsInListingManagerEnabled';
import { OfferInventoryDataResponse, SelectionCategory } from 'modules/shared-menu/types';
import { isSharedMenuBootstrapping, isSharedMenuBootstrappingFailure } from 'modules/shared-menu/utils';

import { BootstrapFailure } from './BootstrapFailure';
import { BootstrapInProgress } from './BootstrapInProgress';
import { BulkActionsToolbar } from './BulkActions/BulkActionsToolbar';
import { canSharedVenueUseBulkActions } from './BulkActions/utils';
import CollectionListErrorBoundary from './CollectionListErrorBoundary';
import { EmptyCollection } from './EmptyCollection';
import { FilterTagsRow } from './FilterTagsRow';
import ListRowRenderer, { ListRowRendererProps } from './ListRowRenderer';
import {
  CollectionContainer,
  ContentContainer,
  Description,
  HeaderContainer,
  HeaderLeft,
  HeaderRight,
  ListContainer,
  ListWrapper,
  Root,
  StyledInput,
  ThumbnailContainer,
  Title,
  TitleContainer,
} from './styled';
import { useIsNarrowView, useVirtualListHeights, getItemKey, useFocusInputOnCtrlF } from './utils';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import useBulkSelection from '../../hooks/useBulkSelection';
import { useOverrideSettings } from '../../hooks/useSelectionOverrideSettings';
import useSharedMenuEditorLists, { ListContentType } from '../../hooks/useSharedMenuEditorLists';
import { Offer, SharedMenuElement } from '../../types';
import { isSharedMenuOffer } from '../../utils';
import { EditOfferSidePanel } from '../EditOfferSidePanel';
import { ImageGallerySidePanel } from '../ImageGallerySidePanel/ImageGallerySidePanel';
import { NewOfferSidePanel } from '../NewOfferSidePanel';
import { NewOptionSidePanel } from '../NewOptionSidePanel';
import { transformOfferInventoryToQuantity } from '../OfferDetailsForm/utils';
import CollectionDnDMonitor from '../dnd/offers/CollectionDnDMonitor';
import { MenuEditorDragData } from '../dnd/types';

type Props = {
  category?: SelectionCategory;
  className?: string;
  description?: string;
  listType: ListContentType;
  onStartMenuBootstrap: () => void;
  rightContent?: React.ReactNode;
  title: string;
  isImageGalleryOpen: boolean;
  onCloseImageGallery: () => void;
};

type CollectionListProps = {
  displayMenuEditorList: SharedMenuElement[];
  draggingId: string | undefined;
  draggingType: MenuEditorDragData['type'] | undefined;
  primaryLanguage: Language;
  listType: ListContentType;
  activeItem: Offer | undefined;
  onSelect: (ids: string[]) => void;
  onDeselect: (ids: string[]) => void;
  selectedIds: Set<string>;
  onOfferClick: (id: string) => void;
  onCopyOffer: (id: string) => void;
  offerInventory?: OfferInventoryDataResponse[];
};

const CollectionList = ({
  displayMenuEditorList,
  draggingId,
  draggingType,
  primaryLanguage,
  activeItem,
  listType,
  onOfferClick,
  onCopyOffer,
  onSelect,
  onDeselect,
  selectedIds,
  offerInventory,
}: CollectionListProps) => {
  const { cbDirection } = useTheme();
  const { listRef, virtualListHeights } = useVirtualListHeights(displayMenuEditorList);
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();
  const sortableContextItems = useMemo(
    () => displayMenuEditorList.map((_, index) => getItemKey(index, displayMenuEditorList)),
    [displayMenuEditorList],
  );

  return (
    <AutoSizer disableWidth>
      {({ height }) => (
        <SortableContext items={sortableContextItems} strategy={verticalListSortingStrategy}>
          <VariableSizeList<ListRowRendererProps['data']>
            ref={listRef}
            style={{ paddingBlockEnd: 105, direction: cbDirection }}
            height={height}
            width="100%"
            itemData={{
              itemList: displayMenuEditorList,
              activeOfferId: activeItem?.id,
              primaryLanguage,
              draggingId,
              draggingType,
              canDrag: !isSharedVenue && menuPermissions.contentEdit,
              canDrop:
                !isSharedVenue &&
                menuPermissions.contentEdit &&
                (listType === 'items-by-category' || listType === 'items-by-subcategory'),
              onOfferClick,
              onCopyOffer,
              onSelect,
              onDeselect,
              selectedIds,
              offerInventory,
            }}
            itemCount={displayMenuEditorList.length}
            itemSize={index => virtualListHeights[index]}
            itemKey={(index, data) => getItemKey(index, data.itemList)}
            overscanCount={6}
          >
            {ListRowRenderer}
          </VariableSizeList>
        </SortableContext>
      )}
    </AutoSizer>
  );
};

const Collection = ({
  category,
  className,
  description,
  listType,
  onStartMenuBootstrap,
  rightContent,
  title,
  isImageGalleryOpen,
  onCloseImageGallery,
}: Props) => {
  const { inputRef } = useFocusInputOnCtrlF();
  const { headerRef, isNarrowView } = useIsNarrowView();
  const intl = useIntl();
  const { selection } = useSelectionData();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedMenu = !vlmeConfig;
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const { offerAttrsEnabledForOverride } = useOverrideSettings();
  const sharedVenueCanUseBulkActions = canSharedVenueUseBulkActions(offerAttrsEnabledForOverride);
  const menuPermissions = useOfferingMenuPermissionsContext();
  const menuFeatures = useOfferingMenuFeaturesContext();
  const { isWoltSpecialsInListingManagerEnabled } = useIsWoltSpecialsInListingManagerEnabled();

  useSharedMenuViewTracking(listType, category?.id);

  const bootstrapInProgress = useMemo(() => isSharedMenuBootstrapping(selection?.draft), [selection?.draft]);

  const bootstrapFailure = useMemo(() => isSharedMenuBootstrappingFailure(selection?.draft), [selection?.draft]);

  const primaryLanguage = selection.primary_language;

  const {
    activeItem,
    appliedFilters,
    handleItemCopy,
    menuEditorList,
    newItemSidePanelProps,
    newOptionSidePanelProps,
    searchFilter,
    setActiveItemId,
    setAppliedFilters,
    setSearchFilter,
    offerInventory,
    refetchOfferInventory,
  } = useSharedMenuEditorLists(listType, {
    showWoltSpecials: menuPermissions.woltSpecials && isWoltSpecialsInListingManagerEnabled,
    categoryId: category?.id,
    hasSubcategories: (category?.subcategories ?? []).length > 0,
  });

  const { selectedIds, selectMultiple, deselectMultiple, clearSelection } = useBulkSelection(menuEditorList);

  const allOffers = useMemo(() => menuEditorList.filter(isSharedMenuOffer), [menuEditorList]);

  const handleSelectAllOffers = useCallback(() => {
    selectMultiple(allOffers.map(offer => offer.itemState.id));
  }, [allOffers, selectMultiple]);

  const {
    handleClearAll: clearAllFilters,
    handleGroupClick: toggleFilterGroup,
    handleItemClick: selectFilter,
    expandedGroups: expandedFilterGroups,
    expandAllGroups: expandAllFilterGroups,
    selectedKeyValues: selectedFiltersByGroup,
    selectedIds: selectedFilterIds,
    handleDeselectItem: deselectFilter,
  } = useSelectableAccordion<SelectableOfferFilterId>(OFFER_FILTER_MENU_ITEMS, setAppliedFilters);

  const offerQuantity = useMemo(() => {
    if (!offerInventory || !activeItem) {
      return;
    }
    const offerQuantity = offerInventory?.find(inventory => String(inventory.offer_id) === activeItem.id);
    return offerQuantity ? transformOfferInventoryToQuantity(offerQuantity) : undefined;
  }, [activeItem, offerInventory]);

  if (bootstrapInProgress && isSharedMenu) {
    return <BootstrapInProgress />;
  }

  if (bootstrapFailure && isSharedMenu) {
    return <BootstrapFailure />;
  }

  return (
    <Root className={className}>
      <CollectionContainer>
        <HeaderContainer ref={headerRef}>
          <HeaderLeft dir="auto" lang={primaryLanguage}>
            {!isNarrowView && category?.image && menuFeatures.categoryImages && (
              <ThumbnailContainer>
                <LazyLoadedThumbnail alt={title} blurhash={category.image_blurhash ?? null} src={category.image} />
              </ThumbnailContainer>
            )}
            <TitleContainer>
              <Title title={title}>{title}</Title>
              {!isNarrowView && description && <Description title={description}>{description}</Description>}
            </TitleContainer>
          </HeaderLeft>
          {!activeItem && (
            <>
              <HeaderRight>
                <StyledInput
                  inputRef={inputRef}
                  placeholder={intl.formatMessage({ id: 'menu.searchInCategory' }, { category: title })}
                  icon={<MagnifyingGlass />}
                  value={searchFilter}
                  onChange={event => setSearchFilter(event.currentTarget.value)}
                />
                <FilterMenu appliedFiltersCount={selectedFilterIds.length}>
                  <SelectableAccordion
                    items={OFFER_FILTER_MENU_ITEMS}
                    expandedGroups={expandedFilterGroups}
                    onItemClick={selectFilter}
                    onGroupClick={toggleFilterGroup}
                    onClearAllClick={clearAllFilters}
                    expandAllGroups={expandAllFilterGroups}
                    selectedIds={selectedFilterIds}
                    expandedByDefault
                  />
                </FilterMenu>
                {rightContent}
              </HeaderRight>
            </>
          )}
        </HeaderContainer>
        {appliedFilters.length > 0 && (
          <FilterTagsRow
            selectedFiltersByGroup={selectedFiltersByGroup}
            onRemoveClick={deselectFilter}
            onClearAll={clearAllFilters}
          />
        )}
        {menuPermissions.contentEdit &&
          allOffers.length > 0 &&
          (!isSharedVenue || (isSharedVenue && sharedVenueCanUseBulkActions)) && (
            <BulkActionsToolbar
              allOffersCount={allOffers.length}
              disabled={!!activeItem}
              onDeselectAll={clearSelection}
              onSelectAll={handleSelectAllOffers}
              selectedIds={selectedIds}
            />
          )}

        <ListContainer>
          <CollectionListErrorBoundary>
            <ListWrapper>
              <CollectionDnDMonitor
                category={category || null}
                menuEditorList={menuEditorList}
                onClearSelection={clearSelection}
              >
                {({ draggingId, draggingType, displayMenuEditorList }) =>
                  displayMenuEditorList.length > 0 ? (
                    <CollectionList
                      draggingId={draggingId}
                      draggingType={draggingType}
                      displayMenuEditorList={displayMenuEditorList}
                      onOfferClick={setActiveItemId}
                      onCopyOffer={handleItemCopy}
                      listType={listType}
                      primaryLanguage={primaryLanguage}
                      activeItem={activeItem}
                      onSelect={selectMultiple}
                      onDeselect={deselectMultiple}
                      selectedIds={selectedIds}
                      offerInventory={offerInventory}
                    />
                  ) : (
                    <EmptyCollection listType={listType} onStartMenuBootstrap={onStartMenuBootstrap} />
                  )
                }
              </CollectionDnDMonitor>
            </ListWrapper>
          </CollectionListErrorBoundary>
        </ListContainer>
      </CollectionContainer>
      <ContentContainer>
        {newOptionSidePanelProps.isOpen && !isSharedVenue && menuPermissions.contentEdit && (
          <NewOptionSidePanel {...newOptionSidePanelProps} />
        )}
        {newItemSidePanelProps.isOpen && !isSharedVenue && menuPermissions.contentEdit && (
          <NewOfferSidePanel {...newItemSidePanelProps} />
        )}
        {!!activeItem && (
          <EditOfferSidePanel
            offer={activeItem}
            onClose={() => setActiveItemId(null)}
            offerQuantity={offerQuantity}
            refetchOfferInventory={refetchOfferInventory}
          />
        )}
        {!activeItem && isImageGalleryOpen && <ImageGallerySidePanel onClose={onCloseImageGallery} />}
      </ContentContainer>
    </Root>
  );
};

export default React.memo(Collection);
